/**
 * ActivitiesApiPort — สัญญาที่ feature ต้องการจาก `member--webapi`
 *
 * รูปเดียวกับ `AuthApiPort`: feature ประกาศว่า **ต้องการอะไร** · แอปเป็นคนเติม adapter
 * ที่ยิง `fetch` จริงให้ตอน composition (`apps/member--web`) ⇒ story/เทสป้อน stub ได้ตรง
 * โดยไม่ต้องมี MSW (ตรงกับที่ `ADR-007` ตัด MSW ออกจากสโคป)
 *
 * 🔓 **2 endpoint แรกเป็น `auth: 'public'`** — adapter **ไม่ต้องแนบ bearer**
 *    (DoD Day 4: *"เปิดดูกิจกรรมได้โดยไม่ต้อง login"* · พิสูจน์ผ่าน api-test แล้ว)
 *    ⇒ ลิงก์ที่แชร์เข้าแชท LINE เปิดได้ทันทีโดยคนที่ยังไม่เคยเป็นสมาชิก
 *
 * 🔒 **`registerForActivity` ต่างออกไป — ต้องแนบ bearer** (Day 5)
 *    ⚠️ **อย่าเผลอทำให้ 2 ตัวแรกแนบ bearer ตามไปด้วย** — วันที่นั้นลิงก์ที่แชร์ให้คนนอก
 *      จะเปิดไม่ได้ ซึ่งเป็นจุดประสงค์ทั้งหมดของการแชร์ (`activities-api.ts` เขียนกำกับไว้แล้ว)
 */
import type { ActivityDetail, ActivitySummary } from './activity.type.js';

/** รูป error ที่ backend คืน — ตรงกับ `errorBody()` ของ `http-adapter-kit` (`{ error: {...} }` ถูกแกะที่ adapter) */
export type ActivitiesApiError = {
  reason: string;
  message: string;
  rule?: string;
};

/**
 * 🔴 ต้องตรงกับ `GetActivityDetailOutcome.reason` ฝั่ง domain **เป๊ะ**
 *
 * ค่านี้คือสิ่งเดียวที่แยก "ไม่มีกิจกรรมนี้" (ผู้ใช้ควรเห็นหน้าที่บอกตรง ๆ + ทางกลับ)
 * ออกจาก "ระบบมีปัญหา" (ควรมีปุ่มลองใหม่) ⇒ พิมพ์ผิดแล้ว **หน้าไม่พัง แต่บอกผิดเรื่อง**
 * ซึ่งตาไม่เห็น — จึงมีเทสของ `useActivityDetail` ยึดค่านี้ไว้
 *
 * ℹ️ ฝั่ง backend รวม "ไม่มี" กับ "ยังไม่เผยแพร่" ไว้ด้วยกันโดยตั้งใจ (ไม่บอกคนนอกว่า
 *    id ไหนมีโอกาสมีอยู่) ⇒ ฝั่งนี้ก็ต้องไม่พยายามแยก
 */
export const ACTIVITY_NOT_FOUND = 'activity_not_found';

/**
 * `reason` ของการลงทะเบียนที่ **หน้าจอต้องแยกแยะจริง ๆ** — ต้องตรงกับ
 * `RegisterForActivityOutcome.reason` ฝั่ง backend เป๊ะ (`registration.contract.ts` map ให้)
 *
 * 🔴 **ไม่ได้ list ครบทุก reason ของ backend โดยตั้งใจ** — ตัวที่ไม่อยู่ในนี้
 *    (`member_not_found` · `free_not_offered`) หน้าจอทำอะไรต่างไปไม่ได้อยู่ดี
 *    ⇒ ตกไปเป็นข้อความจาก backend · เพิ่มเข้ามาเมื่อมี **การกระทำ** ที่ต่างกันจริงเท่านั้น
 *    (list ให้ครบเพื่อความสวยงาม = โค้ดที่ต้องดูแลโดยไม่มีใครได้ประโยชน์)
 */
export const REGISTER_UNAUTHENTICATED = 'unauthenticated';
export const REGISTER_CAPACITY_FULL = 'capacity_full';
export const REGISTER_ALREADY_REGISTERED = 'already_registered';

/** ผลสำเร็จ — `inviteToken` ใช้ประกอบลิงก์ชวนเพื่อน **ของใบลงทะเบียนนี้** (Day 6+) */
export type RegisteredActivity = {
  registrationId: string;
  inviteToken: string;
};

export interface ActivitiesApiPort {
  /** `GET /activities` — ห่อด้วย `{ items }` ฝั่ง wire · adapter แกะให้เป็น array ที่นี่ */
  listActivities(): Promise<
    { ok: true; activities: ActivitySummary[] } | { ok: false; error: ActivitiesApiError }
  >;

  /** `GET /activities/:activityId` — 404 = `reason: 'activity_not_found'` */
  getActivity(
    activityId: string,
  ): Promise<{ ok: true; activity: ActivityDetail } | { ok: false; error: ActivitiesApiError }>;

  /**
   * `POST /activities/:activityId/registrations` — ลงทะเบียนแบบฟรี (Day 5)
   *
   * 🔒 **ต้องแนบ bearer** · 401 → `reason: 'unauthenticated'` (adapter เป็นคนแปลงให้)
   *
   * ⚠️ **ไม่รับ body** — `memberId` มาจาก token ฝั่ง backend เท่านั้น
   *    (ส่ง memberId ไปเอง = ลงทะเบียนแทนคนอื่นได้ · backend ไม่มีที่ให้ใส่อยู่แล้ว)
   *
   * 🔴 **ผลลัพธ์นี้คือ "ด่านจริง"** — ต่างจาก `toRegistrationStatus()` ที่เป็นแค่ snapshot
   *    ของตอนโหลดหน้า · ที่นั่งถูกตัดจริงตรงนี้เท่านั้น (transaction + row lock ฝั่ง backend)
   *    ⇒ กดตอนหน้าบอกว่า "เหลือ 1 ที่นั่ง" แล้วได้ `capacity_full` = **เรื่องปกติ ไม่ใช่บั๊ก**
   */
  registerForActivity(
    activityId: string,
  ): Promise<
    { ok: true; registration: RegisteredActivity } | { ok: false; error: ActivitiesApiError }
  >;
}
